<template>
	<div class="mt-1 pl-4" v-if="!loading">

		<div class="grid col-12 pt-0 pb-0" v-for="(recipient,index) in toRecipientsAndBlankRecord" :key="recipient.contactId">
			<div class="col-2" v-if="index == 0">
				<p class="compass-red">To:</p>
			</div>
			<div class="col-2" v-else />

			<div class="col-8">
				<InputText type="text" v-model="recipient.email" @change="onRecipientEmailChange(this.recipients, recipient,index)" />
			</div>

			<div class="col-2">
				<Button icon="pi pi-trash" @click="removeRecipient(this.recipients, index)" />
			</div>
		</div>

		<div class="grid col-12 pt-0 pb-0" v-for="(recipient,index) in ccRecipientsAndBlankRecord" :key="recipient.contactId">
			<div class="col-2" v-if="index == 0">
				<p class="compass-red">CC:</p>
			</div>
			<div class="col-2" v-else />

			<div class="col-8">
				<InputText type="text" v-model="recipient.email" @change="onRecipientEmailChange(this.ccRecipients, recipient,index)" />
			</div>

			<div class="col-2">
				<Button icon="pi pi-trash" @click="removeRecipient(this.ccRecipients, index)" />
			</div>
		</div>

		<div class="grid col-12">
			<div class="col-2">
				<p class="compass-red">Subject:</p>
			</div>

			<div class="col">
				<InputText type="text" v-model="subject" />
			</div>
		</div>

		<div class="grid col-12">
			<div class="col-2">
				<p class="compass-red">Attached:</p>
			</div>

			<div class="col mt-2 mr-2" v-for="file in attachedFiles" :key="file">
				<span>{{file}}</span>
			</div>
		</div>
	</div>

	<div id="textarea" class="editable-div p-5" contenteditable="true" v-show="!loading">
	</div>

	<div style="min-height:400px;" v-if="loading">
		<ProgressSpinner />
	</div>
</template>

<script lang="ts">
	import { Vue, Options } from 'vue-class-component';
	import { PropertyData, CompanyData, ContactData, GetRenderedEmailTemplateQuery, GenerateAndSendEmailCommand, EmailTemplateType, PropertyServiceData } from '@/api-client/api-client';
	import { format, isValid } from 'date-fns';
	import { CompassApi } from '@/api-client/CompassApiProxy';
	import Toast from '@/utils/Toast';
	import { ConfirmationOptions } from 'primevue/confirmationoptions';

	@Options({
		name: 'EmailTemplate',
		emits: ['updateIsEmailSending', 'updateWasEmailSent', 'updateIsEmailTemplateLoading', 'reloadData', 'updateShow'],
		props: {
			propertyData: null,
			recipientCompanyId: null,
			emailTemplateType: null,
			service: null, // The service clicked when opening this form
			issuedDate: null,
		},
		watch: {
			isEmailSending(value: boolean) {
				this.$emit('updateIsEmailSending', value);
			},
			loading(value: boolean) {
				this.$emit('updateIsEmailTemplateLoading', value);
			}
		}
	})
	export default class EmailTemplate extends Vue {

		// Data
		propertyData: PropertyData = new PropertyData();
		textArea = null;
		recipients: ContactData[] = [];
		ccRecipients: ContactData[] = [];
		attachedFiles: string[] = [];
		subject: string | undefined = undefined;
		supplier: CompanyData = new CompanyData();
		supplierMainContact: ContactData = new ContactData();
		loading = true;
		isEmailSending = false;
		bwofService: PropertyServiceData | undefined = new PropertyServiceData;
		service: PropertyServiceData = new PropertyServiceData;
		hasServices = false;
		issuedDate!: Date;

		recipientCompanyId!: number;
		emailTemplateType: EmailTemplateType | undefined;

		created() {
			this.bwofService = this.propertyData.services?.filter(s => s.serviceId == 1)[0];

			const emailTemplateModel: GetRenderedEmailTemplateQuery = new GetRenderedEmailTemplateQuery();
			emailTemplateModel.propertyId = this.propertyData.propertyId;
			emailTemplateModel.triggeredByPropertyServiceID = this.service.propertyServiceId;
			emailTemplateModel.recipientCompanyId = this.recipientCompanyId;
			emailTemplateModel.emailBodyTemplateName = this.emailTemplateType;

			// Retrieve the generated razor content from server
			const apiClient = CompassApi.emailApi(this.$store);
			apiClient.getRenderedEmailTemplate(emailTemplateModel)
				.then(result => {
					this.subject = result.subject;

					if (result.recipients != null) {
						this.recipients = result.recipients;
						this.recipients.push(new ContactData());
					}

					if (result.ccRecipients != null) {
						this.ccRecipients = result.ccRecipients;
						this.ccRecipients.push(new ContactData());
					}

					this.attachedFiles = result.attachedFiles != null ? result.attachedFiles : this.attachedFiles;

					const textAreaSection = document.getElementById('textarea');

					if (textAreaSection != null && result.renderedEmailContent != null) {
						textAreaSection.innerHTML = result.renderedEmailContent;
					}

					this.hasServices = result.hasServices ? result.hasServices : this.hasServices;
				})
				.catch(error => {
					Toast.ApiError('Error retrieving email template ', error);
				}).finally(() => {
					this.loading = false;
				});
		}

		// Function called by the parent component using refs (in order to place the button in the Popup Footer)
		validateAndSendEmail() {
			// if loading, exit the function
			if (this.loading == true)
				return;

			// If it has services then send email
			if (this.hasServices == true)
				this.sendEmail();

			// Check for confirmation before sending email if there are no services
			if (this.hasServices == false) {
				this.$confirm.require({
					header: 'Email has no valid services',
					message: 'Are you sure you want to send this email to the recipient(s)? This supplier does not have a service due yet.',
					accept: () => {
						this.sendEmail();
					},
					reject: () => {
						// do nothing
					}
				} as ConfirmationOptions);
			}
		}


		sendEmail() {

			if (!this.emailTemplateType) {
				Toast.Error('Email cannot be sent', "Could not identify which email template to use");
				return
			}

			this.isEmailSending = true;

			const emailContent = document.getElementById("textarea")?.outerHTML;

			const requestModel: GenerateAndSendEmailCommand = new GenerateAndSendEmailCommand();
			requestModel.propertyId = this.propertyData.propertyId;
			requestModel.recipientCompanyId = this.recipientCompanyId;
			requestModel.recipients = this.recipients.filter(recipient => recipient.email != null && recipient.email?.trim() != "");
			requestModel.ccRecipients = this.ccRecipients.filter(recipient => recipient.email != null && recipient.email?.trim() != "");
			requestModel.emailSubject = this.subject ?? "";
			requestModel.emailContent = emailContent ?? "";
			requestModel.emailContentTemplate = this.emailTemplateType
			requestModel.issuedDate = this.issuedDate;

			if (this.service && this.service.propertyServiceId) {
				requestModel.propertyServiceId = this.service.propertyServiceId;
			}

			// Retrieve the generated razor content from server
			const apiClient = CompassApi.emailApi(this.$store);
			apiClient.generateAndSendEmail(requestModel)
				.then(result => {

					// Successfully sent the email
					Toast.RemoveAllGroups();
					Toast.Success('Email Generation', "The Email has been sent successfully.");

					// update services WhenLastSupplierEmailSent
					this.propertyData.services?.forEach((service) => {
						const updatedService = result.filter(resultService => resultService.propertyServiceId == service.propertyServiceId)[0];
						service.whenLastSupplierEmailSent = updatedService.whenLastSupplierEmailSent;
					});

					// update wasEmailSent
					this.$emit('updateWasEmailSent', true);

					// Close the modal
					this.$emit('updateShow', false);

					// Reload data
					this.$emit('reloadData', false);
				})
				.catch(error => {
					Toast.ApiError('Error sending email.', error);
				}).finally(() => {
					this.isEmailSending = false;
				});

		}

		formattedDate(date: Date) {
			if (!isValid(date)) return;

			return format(date, 'd MMMM yyyy');
		}

		// If the email is changed to a record with a valid contactId, then we override the ContactData object
		onRecipientEmailChange(recipientsList: ContactData[], recipient: ContactData, index: number) {

			const email = recipient.email?.trim() || undefined;
			const hasValidContactId = !!recipient.contactId && recipient.contactId > 0;
			const hasWhitespaceChange = email !== recipient.email;

			// Check if there is anything to update
			if (!email && !hasWhitespaceChange && !hasValidContactId) {
				return;
			}

			// If emailed was trimmed, or it has valid contact id update it
			const newRecipient = new ContactData();
			newRecipient.email = email;
			recipientsList[index] = newRecipient;
		}

		removeRecipient(recipientsList: ContactData[], index: number) {
			if (recipientsList.length > 1 && index != recipientsList.length - 1)
				recipientsList.splice(index, 1);
		}

		/// Retrieves TO or CC recipient list with a blank record
		retrieveRecipientsAndBlankRecord(recipientList: ContactData[]) {
			if (recipientList.length < 1)
				recipientList.push(new ContactData());

			const lastRecipient = recipientList[recipientList.length - 1]
			if (lastRecipient.email != null && lastRecipient.email?.trim() != "")
				recipientList.push(new ContactData());

			return recipientList;
		}

		/// Retrieves TO recipient list and ensures a blank row in the view
		get toRecipientsAndBlankRecord() {
			return this.retrieveRecipientsAndBlankRecord(this.recipients);
		}

		/// Retrieves CC recipient list and ensures a blank row in the view
		get ccRecipientsAndBlankRecord() {
			return this.retrieveRecipientsAndBlankRecord(this.ccRecipients);
		}
	}
</script>

<style>
	#textarea {
		border: 1px solid #ced4da;
		border-radius: 10px;
	}

		#textarea:hover {
			border: 1px solid #2196F3;
			border-radius: 10px;
		}

		#textarea:focus {
			border: 1px solid #2196F3;
			border-radius: 10px;
			outline: 0 none;
			outline-offset: 0;
			box-shadow: 0 0 0 0.2rem #a6d5fa;
			border-color: #2196F3;
		}
</style>